import { useCallback, useMemo, useState } from 'react';

import {
  selectCounters,
  selectSelectionState,
  selectSummary,
  selectVisibleRows,
  useImportWizard,
} from '../../../features/members-import/state';
import {
  ACCESS_FILTER,
  DEFAULT_REVIEW_PAGE_SIZE,
  REVIEW_STATUS_FILTER,
} from '../constants/reviewFilters.js';
import { IMPORT_ACTIONS } from '../constants/importWizardActions.js';
import { EMPTY_REVIEW_FILTERS } from '../utils/reviewFilters.js';

/**
 * Everything the review step reads or does: the filtered rows and their page, counters,
 * summary, selection and the handlers that dispatch to the shared wizard state. Pagination is
 * client-side (the file has at most 1000 people); Select all covers every page.
 */
export function useReviewTable() {
  const { state, dispatch } = useImportWizard();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_REVIEW_PAGE_SIZE);
  const { search, filters } = state.review;

  const visibleRows = useMemo(() => selectVisibleRows(state), [state]);
  // Bulk actions only reach the rows on screen: a selection made before a search or filter
  // narrowed the list must not act on rows the admin can no longer see.
  const selection = useMemo(() => {
    const visible = new Set(visibleRows.map((row) => row.rowNumber));
    return state.review.selection.filter((n) => visible.has(n));
  }, [state.review.selection, visibleRows]);
  const counters = useMemo(() => selectCounters(state), [state]);
  const summary = useMemo(() => selectSummary(state), [state]);
  const totalPages = Math.max(1, Math.ceil(visibleRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => visibleRows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [visibleRows, currentPage, pageSize],
  );
  const pageRowNumbers = useMemo(() => pageRows.map((row) => row.rowNumber), [pageRows]);
  const selectionState = useMemo(
    () => selectSelectionState(state, pageRowNumbers),
    [state, pageRowNumbers],
  );
  const fileRoles = useMemo(
    () => [...new Set(state.rows.flatMap((row) => row.accessFromFile.roles))].sort(),
    [state.rows],
  );

  // Back to the first page when the search or a filter changes what is listed.
  const [seen, setSeen] = useState({ search, filters });
  if (seen.search !== search || seen.filters !== filters) {
    setSeen({ search, filters });
    setPage(1);
  }

  const act = useCallback((type, payload) => dispatch({ type, payload }), [dispatch]);
  const setSearch = useCallback((query) => act(IMPORT_ACTIONS.SET_REVIEW_SEARCH, query), [act]);
  const setFilters = useCallback((change) => act(IMPORT_ACTIONS.SET_REVIEW_FILTERS, change), [act]);
  const clearAll = useCallback(() => {
    act(IMPORT_ACTIONS.SET_REVIEW_SEARCH, '');
    act(IMPORT_ACTIONS.SET_REVIEW_FILTERS, EMPTY_REVIEW_FILTERS);
  }, [act]);
  const showNewWithoutAccess = useCallback(
    () => act(IMPORT_ACTIONS.SET_REVIEW_FILTERS, { ...EMPTY_REVIEW_FILTERS, access: [ACCESS_FILTER.NONE], status: REVIEW_STATUS_FILTER.NEW }),
    [act],
  );

  const selected = useMemo(() => new Set(selection), [selection]);
  const isRowSelected = useCallback((row) => selected.has(row.rowNumber), [selected]);
  const toggleRow = useCallback(
    (row) =>
      act(
        IMPORT_ACTIONS.SET_REVIEW_SELECTION,
        selected.has(row.rowNumber) ? selection.filter((n) => n !== row.rowNumber) : [...selection, row.rowNumber],
      ),
    [act, selected, selection],
  );
  const togglePage = useCallback(
    () =>
      act(
        IMPORT_ACTIONS.SET_REVIEW_SELECTION,
        selectionState.allOnPage
          ? selection.filter((n) => !pageRowNumbers.includes(n))
          : [...new Set([...selection, ...pageRowNumbers])],
      ),
    [act, selection, selectionState.allOnPage, pageRowNumbers],
  );
  const selectAllRows = useCallback(
    () => act(IMPORT_ACTIONS.SET_REVIEW_SELECTION, visibleRows.map((row) => row.rowNumber)),
    [act, visibleRows],
  );
  const clearSelection = useCallback(() => act(IMPORT_ACTIONS.SET_REVIEW_SELECTION, []), [act]);

  // From the page on screen (clamped), not the raw `page`, so a click always moves the table.
  const prevPage = useCallback(() => setPage(Math.max(1, currentPage - 1)), [currentPage]);
  const nextPage = useCallback(() => setPage(Math.min(totalPages, currentPage + 1)), [currentPage, totalPages]);
  const changePageSize = useCallback((size) => {
    setPageSize(size);
    setPage(1);
  }, []);

  return useMemo(
    () => ({
      state,
      dispatch,
      search,
      filters,
      fileRoles,
      visibleRows,
      pageRows,
      counters,
      summary,
      selection,
      selectionState,
      pagination: { page: currentPage, pageSize, totalPages, total: visibleRows.length },
      setSearch,
      setFilters,
      clearAll,
      showNewWithoutAccess,
      isRowSelected,
      toggleRow,
      togglePage,
      selectAllRows,
      clearSelection,
      prevPage,
      nextPage,
      changePageSize,
    }),
    [
      state, dispatch, search, filters, fileRoles, visibleRows, pageRows, counters, summary, selection,
      selectionState, currentPage, pageSize, totalPages, setSearch, setFilters, clearAll,
      showNewWithoutAccess, isRowSelected, toggleRow, togglePage, selectAllRows, clearSelection,
      prevPage, nextPage, changePageSize,
    ],
  );
}
